import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { setToken } from '../services/tokenManager';
import environment from '../environment/environment';
import axios from 'axios';

const localConfig: UserManagerSettings = {
  authority: "https://identity.nps.gov/",
  client_id: "client_etic_ui_local",
  scope: "openid etic.full_access commons_api.full_access profile email",
  redirect_uri: "http://localhost:3000/#/authcallback",
  post_logout_redirect_uri: "http://localhost:3000/#/",
  response_type: "code",
  filterProtocolClaims: true,
  loadUserInfo: true,
  automaticSilentRenew: false,
  silent_redirect_uri: "http://localhost:3000/silent-refresh.html"
};

const productionConfig: UserManagerSettings = {
  authority: "https://identity.nps.gov/",
  client_id: "client_etic_ui_prod",
  scope: "openid etic.full_access commons_api.full_access profile email",
  redirect_uri: "https://npgallery.nps.gov/inside/etic/#/authcallback",
  post_logout_redirect_uri: "https://npgallery.nps.gov/inside/etic/#/",
  response_type: "code",
  filterProtocolClaims: true,
  loadUserInfo: true,
  automaticSilentRenew: false,
  silent_redirect_uri: "https://npgallery.nps.gov/inside/etic/silent-refresh.html"
};

const config = process.env.NODE_ENV === 'production' ? productionConfig : localConfig;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  handleLoginCallback: () => Promise<void>;
  notification: {
    show: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  };
  clearNotification: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userManager] = useState<UserManager>(() => environment.publicMode ? null as any : new UserManager(config));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!environment.publicMode);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  useEffect(() => {
    if (environment.publicMode) {
      setIsLoading(false);
      return;
    }

    const handleInit = async () => {
      try {
        const hash = window.location.hash;
        if (hash.includes('authcallback') && hash.includes('code=')) {
          const questionMarkIndex = hash.indexOf('?');
          const queryString = hash.substring(questionMarkIndex);
          const callbackUrl = `${window.location.origin}${window.location.pathname}${queryString}`;
          const callbackUser = await userManager.signinRedirectCallback(callbackUrl);
          if (callbackUser) {
            setUser(callbackUser);
            setToken(callbackUser.access_token);
          }
          const returnUrl = localStorage.getItem('redirectUri') || '/';
          localStorage.removeItem('redirectUri');
          window.location.hash = '';
          window.history.replaceState({}, '', returnUrl);
          return;
        }

        const currentUser = await userManager.getUser();
        if (currentUser) {
          setUser(currentUser);
          setToken(currentUser.access_token);
        }
      } catch (error) {
        console.error('Error during auth init:', error);
        setNotification({
          show: true,
          message: 'Error authenticating user',
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    handleInit();

    // Don't register event handlers if in public mode (userManager is null)
    if (environment.publicMode) {
      return;
    }

    const handleUserLoaded = (loadedUser: User) => {
      setUser(loadedUser);
      setNotification({
        show: true,
        message: 'Successfully logged in',
        type: 'success',
      });
      setToken(loadedUser.access_token);
    };

    const handleUserUnloaded = () => {
      setUser(null);
      setNotification({
        show: true,
        message: 'You have been logged out',
        type: 'info',
      });
      setToken(null);
      
      // Clear all authentication data
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      axios.defaults.withCredentials = false;
    };

    const handleSilentRenewError = () => {
      setNotification({
        show: true,
        message: 'Session renewal failed',
        type: 'error',
      });
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [userManager]);

  const login = async () => {
    if (environment.publicMode) return;
    try {
      await userManager.signinRedirect();
    } catch (error) {
      console.error('Error during login:', error);
      setNotification({
        show: true,
        message: 'Login failed',
        type: 'error',
      });
    }
  };

  const clearAllCookies = () => {
    // Clear all accessible cookies (non-httpOnly cookies)
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Delete cookie for current domain and all parent domains
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + window.location.hostname;
    }
  };

  const logout = async () => {
    if (environment.publicMode) return;
    try {
      // Clear all authentication data before redirecting
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      axios.defaults.withCredentials = false;
      setUser(null);
      setToken(null);
      
      // Clear all accessible cookies
      clearAllCookies();
      
      await userManager.signoutRedirect();
    } catch (error) {
      console.error('Error during logout:', error);
      setNotification({
        show: true,
        message: 'Logout failed',
        type: 'error',
      });
    }
  };

  const handleLoginCallback = async () => {
    if (environment.publicMode) return;
    try {
      const hash = window.location.hash;
      const questionMarkIndex = hash.indexOf('?');
      let callbackUrl = window.location.href;
      if (questionMarkIndex !== -1) {
        const queryString = hash.substring(questionMarkIndex);
        callbackUrl = `${window.location.origin}${window.location.pathname}${queryString}`;
      }
      await userManager.signinRedirectCallback(callbackUrl);
    } catch (error) {
      console.error('Error handling callback:', error);
      setNotification({
        show: true,
        message: 'Login callback failed',
        type: 'error',
      });
      throw error;
    }
  };

  const clearNotification = () => {
    setNotification({
      ...notification,
      show: false,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: environment.publicMode ? true : !!user?.access_token,
        isLoading,
        user,
        login,
        logout,
        handleLoginCallback,
        notification,
        clearNotification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
